import { Calendar, Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BOOKING_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const extensionTypes = [
  {
    name: 'Great Lengths Keratin Bonds',
    description: 'Our premium extension method. Great Lengths keratin-bonded extensions use 100% ethically sourced Remy human hair, bonded strand by strand using a patented keratin protein fusion. The bonds are virtually invisible, sit flat against the scalp, and provide the most natural look and movement possible. Great Lengths is the world leader in hair extensions — and we are proud certified specialists.',
    benefits: ['100% Remy human hair', 'Invisible keratin bonds', 'Lasts 4-6 months', 'Ethically sourced', 'Suitable for fine hair'],
    featured: true
  },
  {
    name: 'Tape Extensions',
    description: 'Quick application tape-in extensions perfect for adding volume and length. Ultra-thin, flat wefts are sandwiched between sections of your natural hair using medical-grade adhesive. They lay seamlessly against the head, are comfortable to wear, and the hair is reusable — simply re-tape at maintenance appointments.',
    benefits: ['Quick application (1-2 hours)', 'Reusable hair', 'Easy maintenance every 6-8 weeks', 'Great for volume', 'Comfortable & lightweight'],
    featured: false
  },
  {
    name: 'Weft Extensions',
    description: 'Hand-tied or machine weft extensions sewn into beaded rows for maximum volume and versatility. Ideal for clients wanting a dramatic transformation with a secure, long-lasting result. Wefts provide incredible fullness and natural movement, and can be customised to your exact colour and length.',
    benefits: ['Maximum volume & fullness', 'Natural movement', 'Versatile styling options', 'Long-lasting wear', 'Custom colour matching'],
    featured: false
  }
];

const faqs = [
  {
    q: 'How long do hair extensions last?',
    a: 'Keratin bonds typically last 3-6 months, while tape extensions need maintenance every 6-8 weeks. With proper care, your extensions will look beautiful throughout their lifespan.'
  },
  {
    q: 'Will extensions damage my natural hair?',
    a: 'When applied and maintained correctly by our trained specialists, extensions should not damage your natural hair. We use only premium Great Lengths products and proper application techniques.'
  },
  {
    q: 'Can I colour my extensions?',
    a: 'We recommend having your extensions professionally colour-matched before application. Our specialists can help you achieve the perfect blend with your natural hair colour.'
  },
  {
    q: 'How do I care for my extensions?',
    a: 'We provide complimentary shampoo and conditioner with every extension service, plus a follow-up wash and blow-wave within 2 weeks of installation.'
  }
];

export default function HairExtensions() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="hair-extensions-page">
      <SEO page="hairExtensions" />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=1920&h=800&fit=crop"
            alt="Hair extensions Camberwell - woman with long beautiful hair"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/80" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Premium Great Lengths Extensions</p>
            <h1 className="text-5xl md:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              Best Hair Extensions in Camberwell
            </h1>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
              Transform your look with seamless, high-quality hair extensions at Artistry House. Located just off Burke Road in the heart of Camberwell, our extension specialists create natural, beautiful results using premium Great Lengths products.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-4 text-lg">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Extension Consultation
                </Button>
              </a>
              <Link to="/hair">
                <Button variant="outline" className="border-[#3D352D] text-[#3D352D] hover:bg-[#3D352D] hover:text-white rounded-full px-8 py-4 text-lg">
                  View All Hair Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-[#D4C4B0]/20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-medium text-[#8D7B68] mb-2">Great Lengths</p>
              <p className="text-[#5D4A3A]">Premium Quality Extensions</p>
            </div>
            <div>
              <p className="text-3xl font-medium text-[#8D7B68] mb-2">Expert</p>
              <p className="text-[#5D4A3A]">Certified Specialists</p>
            </div>
            <div>
              <p className="text-3xl font-medium text-[#8D7B68] mb-2">Natural</p>
              <p className="text-[#5D4A3A]">Seamless Results</p>
            </div>
            <div>
              <p className="text-3xl font-medium text-[#8D7B68] mb-2">Aftercare</p>
              <p className="text-[#5D4A3A]">Complimentary Products</p>
            </div>
          </div>
        </div>
      </section>

      {/* Extension Types */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Our Extension Services</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">Types of Hair Extensions</h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              Whether you're looking for added length, volume, or both, we have the perfect extension solution for you.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8">
            {extensionTypes.map((type) => (
              <div key={type.name} className={`p-8 rounded-lg border transition-colors ${type.featured ? 'bg-[#8D7B68]/5 border-[#8D7B68]' : 'bg-white border-[#D4C4B0]/50 hover:border-[#8D7B68]/50'}`}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    {type.featured && <p className="text-xs uppercase tracking-widest text-[#8D7B68] mb-2 font-medium">Our Signature Method</p>}
                    <h3 className="text-2xl font-medium text-[#2C2420] mb-4">{type.name}</h3>
                    <p className="text-[#5D4A3A] leading-relaxed">{type.description}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#8D7B68] mb-3">Key Benefits</p>
                    <ul className="space-y-2">
                      {type.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-center gap-3 text-[#3D352D]">
                          <div className="w-5 h-5 rounded-full bg-[#D4C4B0]/30 flex items-center justify-center">
                            <Check className="w-3 h-3 text-[#8D7B68]" />
                          </div>
                          <span className="text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 md:py-32 bg-[#D4C4B0]/20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">The Artistry House Difference</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                What's Included
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
                Every hair extension service at our Camberwell salon includes premium care and aftercare to ensure your extensions look beautiful and last as long as possible.
              </p>
              <ul className="space-y-4">
                {[
                  'Complimentary consultation and colour matching',
                  'Premium Great Lengths extension hair',
                  'Complimentary shampoo and conditioner to take home',
                  'Complimentary wash & blow-wave within 2 weeks',
                  'Expert application by certified specialists',
                  'Aftercare advice and maintenance tips'
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[#3D352D]">
                    <div className="w-6 h-6 rounded-full bg-[#D4C4B0]/50 flex items-center justify-center">
                      <Check className="w-4 h-4 text-[#8D7B68]" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="aspect-square rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1595476108010-b4d1f102b1b1?w=600&h=600&fit=crop"
                alt="Hair extension application Camberwell salon"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Common Questions</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">Hair Extension FAQs</h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white p-6 rounded-lg border border-[#D4C4B0]/50">
                <h3 className="text-lg font-medium text-[#2C2420] mb-2">{faq.q}</h3>
                <p className="text-[#5D4A3A]">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#2C2420]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-medium text-white mb-6">
            Ready for Your Dream Hair?
          </h2>
          <p className="text-lg text-[#BCAAA4] max-w-2xl mx-auto mb-8">
            Book a consultation at our Camberwell salon and let our extension specialists help you achieve the length and volume you've always wanted.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-10 py-6 text-lg">
              <Calendar className="w-5 h-5 mr-2" />
              Book Extension Consultation
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
